// material-ui
import { Theme, alpha } from '@mui/material/styles';
import { ThemeMode } from '~/types/config';
// types
import { ExtendedStyleProps } from '~/types/extended';
// project import
import getColors from '~/utils/getColors';

function getColorStyle({ color, theme }: ExtendedStyleProps) {
  const colors = getColors(theme, color);
  const { lighter, light, main, dark } = colors;

  return {
    borderColor: alpha(light, 0.5),
    backgroundColor: alpha(lighter, 0.9),
    '& .MuiAlert-icon': {
      color: main,
    },
    '& .MuiAlert-message': {
      color: dark,
    },
  };
}

export default function Alert(theme: Theme) {
  return {
    MuiAlert: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          ...getColorStyle({ color: 'primary', theme }),
          '&.MuiAlert-standardSuccess': getColorStyle({ color: 'success', theme }),
          '&.MuiAlert-standardError': getColorStyle({ color: 'error', theme }),
          '&.MuiAlert-standardWarning': getColorStyle({ color: 'warning', theme }),
          '&.MuiAlert-standardInfo': {
            backgroundColor:
              theme.palette.mode === ThemeMode.DARK
                ? theme.palette.info.lighter
                : theme.palette.info.dark,
            color:
              theme.palette.mode === ThemeMode.DARK
                ? theme.palette.info.dark
                : theme.palette.secondary.dark,
            border: `1px solid ${theme.palette.info.light}`,
            '& .MuiAlert-message': {
              fontSize: '0.875rem',
              color: 'inherit',
            },
            borderRadius: 1,
          },
        },
        icon: {
          fontSize: '1rem',
        },
        message: {
          padding: 0,
          marginTop: 3,
        },
        filled: {
          color: theme.palette.grey[0],
          '&.MuiAlert-filledSuccess': {
            backgroundColor: theme.palette.success.main,
            '& .MuiAlert-message': {
              color: theme.palette.common.white,
            },
            '& .MuiAlert-icon': {
              color: theme.palette.common.white,
            },
          },
          '&.MuiAlert-filledError': {
            backgroundColor: theme.palette.error.main,
            '& .MuiAlert-message': {
              color: theme.palette.common.white,
            },
            '& .MuiAlert-icon': {
              color: theme.palette.common.white,
            },
          },
          '&.MuiAlert-filledWarning': {
            backgroundColor: theme.palette.warning.main,
            '& .MuiAlert-message': {
              color: theme.palette.common.white,
            },
            '& .MuiAlert-icon': {
              color: theme.palette.common.white,
            },
          },
          '&.MuiAlert-filledInfo': {
            backgroundColor: theme.palette.info.main,
            '& .MuiAlert-message': {
              color: theme.palette.common.white,
            },
            '& .MuiAlert-icon': {
              color: theme.palette.common.white,
            },
          },
        },
        border: {
          padding: '10px 16px',
          border: '1px solid',
          ...getColorStyle({ color: 'primary', theme }),
          '&.MuiAlert-borderPrimary': getColorStyle({ color: 'primary', theme }),
          '&.MuiAlert-borderSecondary': getColorStyle({ color: 'secondary', theme }),
          '&.MuiAlert-borderError': getColorStyle({ color: 'error', theme }),
          '&.MuiAlert-borderSuccess': getColorStyle({ color: 'success', theme }),
          '&.MuiAlert-borderInfo': getColorStyle({ color: 'info', theme }),
          '&.MuiAlert-borderWarning': getColorStyle({ color: 'warning', theme }),
        },
        action: {
          '& .MuiButton-root': {
            padding: 2,
            height: 'auto',
            fontSize: '0.75rem',
            marginTop: -2,
          },
          '& .MuiIconButton-root': {
            width: 'auto',
            height: 'auto',
            padding: 2,
            marginRight: 6,
            '& .MuiSvgIcon-root': {
              fontSize: '1rem',
            },
          },
        },
      },
    },
  };
}
